import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { openDB } from 'idb';
import { AUTH_DATABASE_NAME, AUTH_STORE_NAME } from '../dbConstants';
import { authService } from '../services/auth.service';

// Define user type
interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  gender: string;
  mobileNumber: string;
}

// Define context type
interface AuthContextType {
  accessToken: string | null;
  user: User | null;
  setAuthState: (accessToken: string | null, user: User | null) => void;
  clearAuthState: () => void;
  saveRefreshToken: (refreshToken: string, role: string) => Promise<void>;
  getRefreshToken: (role: string) => Promise<string | null>;
  currentRole: string | null;
  setCurrentRole: (role: string) => void;
}

// Create context with default values
const AuthContext = createContext<AuthContextType>({
  accessToken: null,
  user: null,
  setAuthState: () => {},
  clearAuthState: () => {},
  saveRefreshToken: async () => {},
  getRefreshToken: async () => null,
  currentRole: null,
  setCurrentRole: () => {},
});

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [currentRole, setCurrentRole] = useState<string | null>(null);

  // Listen for auth state changes from the auth service
  useEffect(() => {
    const syncWithAuthService = () => {
      const authState = authService.getAuthState();
      setAccessToken(authState.accessToken);
      setUser(authState.user);
      setCurrentRole(authState.currentRole);
    };

    // Initial sync
    syncWithAuthService();

    // Listen for changes
    const unsubscribe = authService.onAuthStateChange(syncWithAuthService);

    return () => {
      unsubscribe();
    };
  }, []);

  // Listen for cross-tab authentication events (logout/login from other tabs)
  useEffect(() => {
    const handleStorageChange = async (event: StorageEvent) => {
      // Only respond to auth-event changes
      if (event.key !== 'auth-event') return;

      // Parse the event data
      if (event.newValue) {
        try {
          const eventData = JSON.parse(event.newValue);

          if (eventData.type === 'LOGOUT') {
            // User logged out in another tab - clear local state and redirect
            await authService.clearAllData();
            window.location.href = '/login';
          } else if (eventData.type === 'LOGIN') {
            // User logged in another tab - reload to sync auth state
            window.location.reload();
          }
        } catch (error) {
          console.error('Error handling cross-tab auth event:', error);
        }
      }
    };

    // Add storage event listener for cross-tab communication
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Initialize IndexedDB
  const initDB = async () => {
    return openDB(AUTH_DATABASE_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(AUTH_STORE_NAME)) {
          db.createObjectStore(AUTH_STORE_NAME, { keyPath: 'key' });
        }
      },
    });
  };

  // Save refresh token to IndexedDB with role prefix
  const saveRefreshToken = async (refreshToken: string, role: string) => {
    try {
      const db = await initDB();
      await db.put(AUTH_STORE_NAME, { 
        tokenValue: refreshToken, 
        key: `${role}_refreshToken` 
      });
    } catch (error) {
      console.error('Error saving refresh token to IndexedDB:', error);
    }
  };

  // Get refresh token from IndexedDB with role prefix
  const getRefreshToken = async (role: string): Promise<string | null> => {
    try {
      const db = await initDB();
      const token = await db.get(AUTH_STORE_NAME, `${role}_refreshToken`);
      return token?.tokenValue || null;
    } catch (error) {
      console.error('Error getting refresh token from IndexedDB:', error);
      return null;
    }
  };

  // Set auth state (both access token and user)
  const setAuthState = (newAccessToken: string | null, newUser: User | null) => {
    setAccessToken(newAccessToken);
    setUser(newUser);
    
    if (newUser) {
      setCurrentRole(newUser.role);
    }
  };

  // Clear auth state
  const clearAuthState = async () => {
    setAccessToken(null);
    setUser(null);
    
    // Clear refresh tokens from IndexedDB if needed
    if (currentRole) {
      try {
        const db = await initDB();
        await db.delete(AUTH_STORE_NAME, `${currentRole}_refreshToken`);
      } catch (error) {
        console.error('Error clearing refresh token from IndexedDB:', error);
      }
    }
    
    setCurrentRole(null);
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        user,
        setAuthState,
        clearAuthState,
        saveRefreshToken,
        getRefreshToken,
        currentRole,
        setCurrentRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => useContext(AuthContext); 